import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import {
    getSearchEngineErrorMessage,
    SearchEngineIndex,
} from 'components/providers/searchEngine/searchEngineApi'
import { useEffect, useState } from 'react'

interface Props {
    providerId: number
    index: SearchEngineIndex
}

type FetchState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'loaded'; documents: unknown }

function isNotIndexed(documents: unknown): boolean {
    return documents === null || (Array.isArray(documents) && documents.length === 0)
}

const SearchEngineDocumentsModal = NiceModal.create(({ providerId, index }: Props) => {
    const modal = useModal()
    const [state, setState] = useState<FetchState>({ status: 'loading' })

    useEffect(() => {
        let isCancelled = false
        index
            .fetchDocuments(providerId)
            .then((documents) => !isCancelled && setState({ status: 'loaded', documents }))
            .catch(
                (error: unknown) =>
                    !isCancelled &&
                    setState({ status: 'error', message: getSearchEngineErrorMessage(error) }),
            )
        return () => {
            isCancelled = true
        }
    }, [index, providerId])

    return (
        <BaseModal maxWidth={900}>
            <BaseModalHeader>
                <h3 className="m-0">
                    {index.label} — provider {providerId}
                </h3>
            </BaseModalHeader>
            <BaseModalMainContent>
                {state.status === 'loading' && <LoadingDots />}
                {state.status === 'error' && <p className="m-0 text-error">{state.message}</p>}
                {state.status === 'loaded' && isNotIndexed(state.documents) && (
                    <p className="m-0">This provider has no documents in the {index.label}.</p>
                )}
                {state.status === 'loaded' && !isNotIndexed(state.documents) && (
                    <pre className="m-0 whitespace-pre-wrap break-all text-sm">
                        {JSON.stringify(state.documents, null, 2)}
                    </pre>
                )}
            </BaseModalMainContent>
            <BaseModalFooter className="flex justify-end">
                <Button appearance="secondary" onClick={() => modal.hide()}>
                    Close
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default SearchEngineDocumentsModal
